import { useEffect, useState } from 'react';
import { api, darf, zeitpunkt, type Anmelder } from '../api.ts';
import Abwurf from '../teile/Abwurf.tsx';
import {
  Dialog, Fehlerkasten, Text, ausFehler, type Fehlerlage,
} from '../teile/Formular.tsx';
import { MitGrund } from './Aufmass.tsx';
import ImportVorschau from './Import.tsx';
import Leseregel from './Leseregel.tsx';
import { Transferverzeichnis }
  from './Uebernahme.tsx';
import { useFrisch } from '../teile/Frisch.tsx';

/**
 * Der Posteingang.
 *
 * ORIGINALTEXT MARVIN (24.08.2026)
 *   „der „upload Ordner" fällt ja weg. wie machen wir denn hier den upload der
 *    dateien ins system, zumal die daten ja bisher über n8n aus den mails in
 *    den drive geschoben wurden und dann den aufträgen zugeodnet wurden. das
 *    müssen wir hier ja berücksichtigen. was sich ändert oder frühere läufe an
 *    änderungen brauchen wir nicht. wir brauchen ein solides import aller
 *    dateien."
 *
 * Deshalb steht hier KEINE Änderungsliste und KEIN Laufprotokoll.
 *
 *   ORIGINALTEXT MARVIN (24.08.2026)
 *   „er soll die dateien im hintergrund selbst einlesen und verarbeiten, ich
 *    will das nicht im system sehen oder manuell machen."
 *
 * Und deshalb ist dieser Bildschirm keine Arbeitsliste mehr. Der Server legt
 * beim Ankommen selbst ab, was er sicher zuordnen kann, und holt alle fünf
 * Minuten nach, was inzwischen zuordenbar geworden ist (eine Anordnung kommt
 * regelmässig VOR ihrem Auftrag an).
 *
 * Was hier steht, ist nur noch das, was WIRKLICH eine Entscheidung braucht.
 * Der Menüpunkt taucht auch nur dann auf. Das ist kein Widerspruch zu „nicht
 * im System sehen": eine Datei, die sich nicht zuordnen lässt und die niemand
 * sieht, ist eine verlorene Datei.
 */

const ZUSTAENDE = [
  { wert: 'offen',       text: 'Offen' },
  { wert: 'einsortiert', text: 'Einsortiert' },
  { wert: 'verworfen',   text: 'Verworfen' },
];

const HERKUNFT_TEXT: Record<string, string> = {
  hand: 'von Hand',
  mail: 'aus der Mail',
};

/** Eine Tabelle gehört zu keinem einzelnen Auftrag. */
// WAS EINGELESEN WIRD STATT ABGELEGT. Seit Migration 043 gehört die
// Kundenliste dazu: sie legt keine Datei in eine Akte, sondern füllt leere
// Kundenfelder an vorhandenen Aufträgen.
const TABELLE = ['abruf', 'bafa', 'kunden'];

/* HIER STAND EINE LISTE AUS 3 ARTEN, an denen sich ein Leser anlernen liess.
   Sie ist weg: angelernt wird an jedem Papier, das keine Tabelle ist, und die
   Art wählt der Leser selbst. Der Grund steht am Knopf und in
   `routen/leseregel.ts`. */

/**
 * Was der Lauf zuletzt getan hat, in einem Satz.
 *
 * DAS IST DIE FRAGE, DIE DIESER BILDSCHIRM BISHER NICHT BEANTWORTET HAT.
 * „Der Takt ist abgeschaltet", „der Lauf ist gescheitert" und „es gab nichts
 * zu tun" sahen alle drei gleich aus: die Datei liegt einfach da. Ein Lauf,
 * von dem niemand sehen kann, ob er lief, ist von einem abgeschalteten nicht
 * zu unterscheiden.
 *
 * Die Reihenfolge der Fälle ist die der Dringlichkeit: erst was kaputt ist,
 * dann was abgeschaltet ist, dann der Normalfall.
 */
function laufsatz(d: any): string {
  const l = d?.lauf;
  if (!l) {
    return d?.taktMinuten === 0
      ? 'Der Lauf ist abgeschaltet (Takt 0 Minuten).'
      : 'Noch kein Lauf. Neue Dateien werden beim Ankommen eingelesen.';
  }
  const wann = zeitpunkt(l.begonnen_am);
  if (!l.beendet_am) return `Lauf seit ${wann} unterwegs.`;
  if (l.fehler?.length) {
    return `Zuletzt ${wann}: ${l.fehler.length === 1 ? 'ein Fehler' : l.fehler.length + ' Fehler'}.`;
  }
  const teile: string[] = [];
  if (l.tabellen) {
    teile.push(`${l.tabellen} ${l.tabellen === 1 ? 'Tabelle' : 'Tabellen'} eingelesen`);
    // Die Zahlen nur, wenn sie etwas sagen. „0 angelegt, 0 geändert" neben
    // „1 Tabelle eingelesen" liest sich wie ein Fehlschlag und war keiner.
    if (l.angelegt || l.geaendert) {
      teile.push(`${l.angelegt} angelegt, ${l.geaendert} geändert`);
    }
  }
  if (l.abgelegt) teile.push(`${l.abgelegt} abgelegt`);
  if (l.liegt) teile.push(`${l.liegt} liegt noch`);
  return `Zuletzt ${wann}: ${teile.length ? teile.join(', ') + '.' : 'nichts zu tun.'}`;
}

function groesse(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
  return (bytes / 1024 / 1024).toFixed(1).replace('.', ',') + ' MB';
}

/** „3 Minuten" seit einem Zeitpunkt. */
function minutenSeit(iso: string): string {
  const m = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60_000));
  return m < 1 ? 'unter 1 Minute' : m === 1 ? '1 Minute' : `${m} Minuten`;
}

export default function Eingang({ ich }: { ich: Anmelder }) {
  const [zustand, setZustand] = useState('offen');
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [angekommen, setAngekommen] = useState<any[] | null>(null);
  /** Jemand hat einen ORDNER abgeworfen. Dann klappt der Umzugsweg auf. */
  /** Was der zweite Blick ergeben hat. Bleibt stehen, bis jemand weitergeht. */
  const [nochmal, setNochmal] = useState<any>(null);
  const [verwerfen, setVerwerfen] = useState<any>(null);
  // Die Tabellen, die gerade eingelesen werden sollen. Der Import ist kein
  // eigener Bildschirm mehr; er ist der zweite Schritt für genau diese Zeilen.
  const [tabellen, setTabellen] = useState<string[] | null>(null);
  // Die Zeile, für die gerade ein Auftrag gesucht wird.
  const [zuordnen, setZuordnen] = useState<any>(null);
  // Die Zeile, an der gerade ein Leser angelernt wird.
  const [anlernen, setAnlernen] = useState<any>(null);

  const darfEinsortieren = darf(ich, 'eingang.einsortieren');
  const darfAnlernen = darf(ich, 'leseregel.pflegen');
  const darfAnnehmen = darf(ich, 'eingang.annehmen');

  async function laden() {
    try { setD(await api.eingang(zustand)); setFehler(null); }
    catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); }, [zustand]);
  useFrisch(laden);

  async function annehmen(neue: File[]) {
    if (!neue.length) return;
    setLaeuft(true); setFehler(null); setAngekommen(null);
    try {
      const a = await api.eingangAnnehmen(neue);
      setAngekommen(a.angekommen);
      setZustand('offen');
      await laden();
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  async function einsortieren(e: any, auftragId?: string) {
    setLaeuft(true); setFehler(null);
    try { await api.eingangEinsortieren(e.id, auftragId); await laden(); }
    catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  /**
   * Den Hintergrundlauf früher anstossen.
   *
   * Er läuft ohnehin alle fünf Minuten. Dieser Knopf ist für den Fall, dass
   * gerade ein Auftrag entstanden ist und man wissen will, ob der Bescheid
   * dazu jetzt passt.
   */
  async function jetztVerarbeiten() {
    setLaeuft(true); setFehler(null);
    try { await api.eingangVerarbeiten(); await laden(); }
    catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  /**
   * Die Art derer neu bestimmen, die der Server selbst nicht erkannt hat.
   *
   * Das Ergebnis bleibt stehen, bis der Bildschirm gewechselt wird. Ein Lauf,
   * der 12 Dateien ansieht und danach nur eine kürzere Liste zeigt, hat den
   * Menschen davor raten lassen, was er getan hat.
   */
  async function nochmalAnsehen() {
    setLaeuft(true); setFehler(null); setNochmal(null);
    try { setNochmal(await api.eingangNochmal()); await laden(); }
    catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  /* DER STAND DES NEU-LESENS: alle drei Sekunden, solange er läuft. */
  const [neuLesen, setNeuLesen] = useState<any>(null);
  useEffect(() => {
    if (!neuLesen?.laeuft) return;
    const t = setTimeout(async () => {
      try { const s2 = await api.eingangAllesNeuLesenStand(); setNeuLesen(s2); if (!s2.laeuft) await laden(); } catch { /* weiter */ }
    }, 3000);
    return () => clearTimeout(t);
  }, [neuLesen]);
  useEffect(() => { api.eingangAllesNeuLesenStand().then((s2) => { if (s2.laeuft) setNeuLesen(s2); }).catch(() => {}); }, []);
  async function allesNeuLesen() {
    setFehler(null);
    try { setNeuLesen(await api.eingangAllesNeuLesen()); }
    catch (f) { setFehler(ausFehler(f)); }
  }

  const zahlen = d?.zahlen;
  const zeilen: any[] = d?.zeilen ?? [];
  const arten: Record<string, string> = d?.arten ?? {};
  const offeneTabellen = zustand === 'offen'
    ? zeilen.filter((z) => TABELLE.includes(z.art)) : [];

  return (
    <>
      <div className="kopf">
        {/* DAS WORT „IMPORT" STEHT JETZT DA.
            Marvin hat DREIMAL danach gefragt - am 24.08., am 25.08. („wo ist
            das?") und noch einmal am 25.08. („und wo ist nun der import der
            daten?"). Der Weg gab es die ganze Zeit und er funktioniert; nur
            hiess der Bildschirm „Unterlagen", der Knopf an der Tabellenzeile
            hiess „Ansehen", und das Modul `import` stand auf
            `im_menue = false`.
            DREIMAL NACHGEFRAGT HEISST NICHT, DASS DIE ERKLÄRUNG FEHLTE - es
            heisst, dass die BESCHRIFTUNG fehlte. Wer ein Wort sucht, findet
            keinen Weg, auf dem es nicht steht. */}
        {/* IN DIE ÜBERSCHRIFT GEHÖRT DER NAME DES BILDSCHIRMS UND SONST NICHTS.
            ORIGINALTEXT MARVIN (27.08.2026)
              „die infos gehören nich in dien titel."
            Der Laufstand stand hier eine Version lang und war an der falschen
            Stelle: eine Überschrift sagt, WO man ist, und ändert sich nicht
            alle fünf Minuten. Er steht jetzt unten bei der Liste, zu der er
            gehört. */}
        <h1>Unterlagen und Import</h1>
        <div className="wachsen" />
        {/* Kein „alles ablegen" mehr und kein „Tabelle einlesen": beides macht
            der Server von selbst. Der Knopf hier stösst den Lauf nur früher
            an, für den, der nicht fünf Minuten warten will. */}
        {darfEinsortieren && (
          <button className="knopf" onClick={jetztVerarbeiten} disabled={laeuft}>
            {laeuft ? 'Läuft' : 'Jetzt nachsehen'}
          </button>
        )}
        {/* ALLES NEU LESEN (.272). Marvin: „er soll die hochgeladenen dateien
            nun nochmal mit OCR scannen und wegsortieren bevor ich manuell dran
            gehe." */}
        {darfEinsortieren && (
          <button className="knopf haupt" disabled={laeuft || !!neuLesen?.laeuft} onClick={allesNeuLesen}
                  title="Jede offene Datei noch einmal lesen (Scans per Texterkennung) und einsortieren; lose Behördendateien in den Ordnern werden erfasst, Doppelte entfernt.">
            {neuLesen?.laeuft ? (neuLesen.schritt === 'aufraeumen' ? 'Räumt die Ordner auf' : `Liest ${neuLesen.fertig} von ${neuLesen.gesamt}`) : 'Alles neu lesen und einsortieren'}
          </button>
        )}
      </div>

      <div className="inhalt">
        <Fehlerkasten lage={fehler} />
        {neuLesen && (neuLesen.laeuft || neuLesen.fertig > 0) && (
          <div className={'hinweis' + (neuLesen.laeuft ? '' : ' erledigt')}>
            {neuLesen.laeuft
              ? (neuLesen.schritt === 'aufraeumen'
                  ? <>Alle {neuLesen.gesamt} Dateien gelesen, {neuLesen.abgelegt} einsortiert. Jetzt werden die Ordner dieser Aufträge aufgeräumt
                      {neuLesen.teil ? <>: Auftrag {neuLesen.teil} von {neuLesen.teil_von}</> : null}
                      {neuLesen.aufraeumen_seit ? <> (seit {minutenSeit(neuLesen.aufraeumen_seit)})</> : null}.
                      {/* DER BALKEN (.293). Marvin: „ich sehe leider kein
                          fortschrittsbalken, ist er fertig, hängt er, läd er?" */}
                      <div className="balken dick" style={{ marginTop: 8 }}>
                        <div style={{ width: `${Math.round(100 * (neuLesen.teil ?? 0) / Math.max(1, neuLesen.teil_von ?? 1))}%`, background: 'var(--akzent)' }} />
                      </div></>
                  : <>Alles wird neu gelesen: {neuLesen.fertig} von {neuLesen.gesamt} Dateien, {neuLesen.abgelegt} einsortiert.
                      {neuLesen.aktuell ? <> Gerade: „{neuLesen.aktuell}".</> : null} Die Texterkennung braucht je Scan einige Sekunden.
                      <div className="balken dick" style={{ marginTop: 8 }}>
                        <div style={{ width: `${Math.round(100 * neuLesen.fertig / Math.max(1, neuLesen.gesamt))}%`, background: 'var(--akzent)' }} />
                      </div></>)
              : <>Fertig: {neuLesen.gesamt} Dateien neu gelesen, {neuLesen.abgelegt} einsortiert.
                  {(neuLesen.haengt ?? []).length > 0 && <> Nicht fertig geworden: {neuLesen.haengt.join(', ')}.</>}</>}
            {neuLesen.aufgeraeumt && (
              <> In den Ordnern: {neuLesen.aufgeraeumt.erfasst} Behördendateien als Papier erfasst,
                {' '}{neuLesen.aufgeraeumt.doppelt_weg} Doppelte entfernt{neuLesen.aufgeraeumt.scan_weg ? <> (davon {neuLesen.aufgeraeumt.scan_weg} Scans, weil die Mail schon da war)</> : null}, {neuLesen.aufgeraeumt.zeitraum} Zeiträume nachgelesen.</>
            )}
          </div>
        )}

        {/* WAS OFFEN IST, STEHT VOR DEM, WAS EIN WERKZEUG IST.
            ================================================================
            ORIGINALTEXT MARVIN (08.09.2026)
              „hier weiß ich nicht wo ich klicken muss"

            Beide Karten standen am FUSS dieses Bildschirms, hinter der
            Eingangsliste und hinter drei Werkzeugen, die man einmal im Leben
            braucht. **Ein Weg, den man nicht findet, ist so gut wie keiner** -
            derselbe Befund wie am 07.09.2026 gleich dreimal (die Rechtekarte
            hinter einem kleinen Knopf, die Koordinatensuche hinter einem Knopf
            je Auftrag, die Anleitung in einer Datei auf dem Server).

            Sie erscheinen ohnehin nur, wenn wirklich etwas offen ist - also
            stehen sie oben, genau dann, wenn es etwas zu tun gibt. Ein
            Werkzeug, das man sucht, sucht man von oben nach unten; eine
            Arbeit, von der man nichts weiss, findet man nur, wenn sie im Weg
            steht. */}
        {/* „BAUAKTEN AUSLESEN" IST WEG (.271). Marvin, 01.10.2026: „das thema
            bauakten einlesen, ist aus meiner sicht nicht zu gebrauchen. wir
            machen doch alles mit dem drag and drop." */}
        {/* HIER STAND DIE KARTE „Papiere und Dateien zusammenbringen" (.277).
            Marvin, 02.10.2026: „habe ich noch nie genutzt ... ist das
            doppelter inhalt? kann das gelöscht werden?" Ja: der Knopf ging
            denselben Weg, den Hochladen und Erfassen ohnehin gehen. Der Rest
            läuft jetzt im Hintergrund (`verknuepfeBestand`). */}

        {/* ------------------------------------------------ Hineinlegen */}
        {darfAnnehmen && (
          <Abwurf
            titel="Bescheide, Bauakten, Fotos, Abruf, BAFA und Sammeldruck"
            laeuft={laeuft}
            laufText="Wird angenommen"
            /* KEIN LEHRTEXT ÜBER DER ABWURFFLÄCHE.
               ORIGINALTEXT MARVIN (27.08.2026)
                 „ich brauch keine erkläreungen undinfos die schon ersichtlich
                  sind. das system ist extra dafür entwickelt."
               Hier standen fünf Zeilen, die bei JEDEM Aufruf erklärten, wie
               die Erkennung arbeitet. Beim ersten Mal ist das eine Auskunft,
               ab dem zweiten Mal ist es Fläche, die den Blick von der Liste
               nimmt. Es steht im Handbuch, Kapitel „Warum Sie beim Import
               nichts mehr anklicken müssen". Was hier bleibt, ist der einzige
               Satz, der beim Hinsehen NICHT ersichtlich ist: dass ein ganzer
               Ordner nicht hierher gehört. */
            hinweis={'Ein ganzer Ordner gehört nicht hierher - hier kommen '
                     + 'einzelne Papiere an.'}
            nimm={(x) => {
              /* EIN ORDNER GEHÖRT NICHT IN DEN POSTEINGANG.
                 Er nimmt EINZELNE Papiere und entscheidet je Stück; ein
                 Ordnerbaum trägt seine Zuordnung schon im Ordnernamen. Wer
                 einen abwirft, wird nach unten geschickt statt abgewiesen. */
              const ausOrdner = x.some(
                (b) => b.pfad.replace(/\\/g, '/').includes('/'));
              annehmen(x.map((b) => b.datei));
            }} />
        )}

        {/* DER DATEIUMZUG STAND HIER UND IST GELAUFEN.
            Marvin, 27.08.2026: „der import ist gelaufen die dateien und die
            einlesenfunktion kann weg." Er war der wichtigste Weg auf diesem
            Bildschirm, solange er nicht gelaufen war; jetzt ist er es nicht
            mehr. Ein Knopf für eine einmalige Sache ist ab dem zweiten Tag
            eine Gefahr und kein Werkzeug. Was hereinkommt, kommt über den
            Posteingang oben und über den Dateireiter im Auftrag. */}

        {/* WAS ZUM EINLESEN BEREITLIEGT, STEHT GANZ OBEN.
            Eine Abruf- oder BAFA-Tabelle ist etwas anderes als ein Bescheid:
            sie legt nicht eine Datei ab, sondern schreibt Hunderte von
            Aufträgen. Bis heute stand sie in derselben Liste wie alles andere
            und trug einen Knopf mit der Aufschrift „Ansehen".
            Zwei Zeilen einlesen heisst zweimal denselben Weg gehen - deshalb
            nimmt der Knopf ALLE mit, die geradeliegen. */}
        {/* EINE TABELLE, DIE HIER NOCH LIEGT, IST EIN BEFUND UND KEINE AUFGABE.
            Seit dem 27.08.2026 wird sie beim Ankommen sofort eingelesen. Steht
            sie trotzdem hier, ist entweder der Schalter aus oder der Lauf
            gescheitert - und dann ist die Frage nicht „einlesen?", sondern
            „warum nicht von selbst?". Der Knopf bleibt, weil eine Antwort
            ohne Ausweg keine Hilfe ist. */}
        {offeneTabellen.length > 0 && (
          <div className={'hinweis' + (d?.lauf?.fehler?.length ? ' warnung' : '')}
               style={{ marginTop: 16 }}>
            <div className="reihe">
              <div style={{ flex: 1 }}>
                <b>
                  {offeneTabellen.length}{' '}
                  {offeneTabellen.length === 1 ? 'Tabelle liegt' : 'Tabellen liegen'}
                  {' '}noch hier
                </b>
                <div className="klein leise">
                  {offeneTabellen.map((z: any) => z.dateiname).join(', ')}
                </div>
                {d?.lauf?.fehler?.length ? (
                  <div className="klein" style={{ marginTop: 4 }}>
                    Der letzte Lauf ist daran gescheitert:{' '}
                    {d.lauf.fehler.join(', ')}
                  </div>
                ) : d?.taktMinuten === 0 ? (
                  <div className="klein" style={{ marginTop: 4 }}>
                    Der Takt steht auf 0 Minuten, also ist er abgeschaltet.
                    Das ist eine Einstellung unter „Konfiguration",
                    Bereich „Posteingang".
                  </div>
                ) : null}
              </div>
              <button className="knopf haupt" disabled={laeuft}
                      onClick={() => setTabellen(offeneTabellen.map((z: any) => z.id))}>
                Einlesen
              </button>
            </div>
          </div>
        )}

        {laeuft && !d && (
          <p className="klein leise" style={{ marginTop: 12 }}>Wird gelesen.</p>
        )}

        {/* Was gerade angekommen ist. Steht getrennt über der Liste, weil beim
            Hochladen von zwanzig Dateien sonst nicht zu sehen wäre, was davon
            erkannt wurde. */}
        {angekommen && (
          <div className="karte" style={{ marginTop: 16 }}>
            <h3>Gerade angekommen ({angekommen.length})</h3>
            <div className="leib">
              <table className="liste">
                <tbody>
                  {angekommen.map((a: any) => (
                    <tr key={a.id} style={{ cursor: 'default' }}>
                      <td>{a.dateiname}</td>
                      <td style={{ width: 220 }}>
                        <span className={'marke-zustand '
                          + (a.art === 'unbekannt' ? 'z-neu' : 'z-laufend')}>
                          {a.art_text}
                        </span>
                      </td>
                      <td className="klein leise">
                        {a.doppelt
                          ? 'Lag schon im Eingang, nicht doppelt angelegt.'
                          : (a.zuordnung_wie ?? a.grund ?? '')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ------------------------------------------------------ Die Liste */}
        <div className="reihe reiterreihe">
          {ZUSTAENDE.map((z) => (
            <button key={z.wert} className="knopf" aria-current={zustand === z.wert}
                    onClick={() => setZustand(z.wert)}>
              {z.text}{' '}
              {zahlen ? (zahlen as any)[
                z.wert === 'offen' ? 'offen'
                : z.wert === 'einsortiert' ? 'einsortiert' : 'verworfen'] : ''}
            </button>
          ))}
          <div className="wachsen" />
          {zustand === 'offen' && (zahlen?.unerkannt ?? 0) > 0 && (
            <span className="klein leise">
              {zahlen.unerkannt} nicht erkannt
            </span>
          )}
          {/* NOCH EINMAL ANSEHEN.

              ORIGINALTEXT MARVIN (26.08.2026)
                „muss ich die datei nun nochmal hochladen, odeer verwerfen oder
                 erkennt er sie nun automatisch und spielt sie ein?"

              Die ehrliche Antwort war: von allein passiert nichts. Die Art
              wurde beim Ankommen bestimmt und wird nie neu bestimmt, damit
              kein Menschenurteil überschrieben wird. Nur galt das auch für
              die Dateien, an denen sich der Server selbst verschätzt hat.

              Der Knopf steht nur da, wenn es etwas zu tun gibt, und er sagt
              vorher, wie viel. Ein Knopf, der immer da ist und meistens nichts
              tut, wird nicht gedrückt, wenn es darauf ankommt. */}
          {zustand === 'offen' && darfEinsortieren
            && (zahlen?.nochmal ?? 0) > 0 && (
            <button className="knopf" onClick={nochmalAnsehen} disabled={laeuft}
                    title={'Die Erkennung ist seither besser geworden. Was ein '
                         + 'Mensch von Hand gesetzt hat, wird dabei nie '
                         + 'angefasst.'}>
              {zahlen.nochmal} noch einmal ansehen
            </button>
          )}
        </div>

        {nochmal && (
          <div className={'hinweis' + (nochmal.erkannt ? '' : ' warnung')}
               style={{ marginBottom: 10 }}>
            <b>Zweiter Blick</b>{nochmal.klartext}
            {nochmal.zeilen.filter((z: any) => z.art !== 'unbekannt').length > 0 && (
              <div className="klein" style={{ marginTop: 6 }}>
                {nochmal.zeilen.filter((z: any) => z.art !== 'unbekannt')
                  .slice(0, 8)
                  .map((z: any) => `${z.dateiname}: ${z.art_text}`)
                  .join(', ')}
              </div>
            )}
            {nochmal.zeilen.some((z: any) => z.fehler) && (
              <div className="klein" style={{ marginTop: 6,
                                              color: 'var(--ueberfaellig)' }}>
                {nochmal.zeilen.filter((z: any) => z.fehler).length} Datei(en)
                ließen sich nicht lesen. Sie bleiben liegen.
              </div>
            )}
          </div>
        )}

        <div className="karte">
          <div className="leib" style={{ padding: 0 }}>
            {zeilen.length === 0 ? (
              <p className="klein leise" style={{ padding: '18px 16px', margin: 0 }}>
                {zustand === 'offen'
                  ? 'Nichts liegen geblieben. Alles, was ankam, ist zugeordnet.'
                  : 'Hier steht noch nichts.'}
              </p>
            ) : (
              <table className="liste">
                <thead>
                  <tr>
                    <th>Datei</th>
                    <th style={{ width: 200 }}>Erkannt als</th>
                    <th>Auftrag</th>
                    <th style={{ width: 150 }}>Angekommen</th>
                    {zustand === 'offen' && darfEinsortieren &&
                      <th style={{ width: 210 }} />}
                  </tr>
                </thead>
                <tbody>
                  {zeilen.map((z) => {
                    const tabelle = TABELLE.includes(z.art);
                    const sicher = !!z.auftrag_id;
                    return (
                      <tr key={z.id} style={{ cursor: 'default' }}>
                        <td>
                          {z.dateiname}
                          <div className="klein leise">
                            {groesse(z.groesse)}, {HERKUNFT_TEXT[z.herkunft]}
                            {z.absender ? ` von ${z.absender}` : ''}
                          </div>
                          {z.betreff && (
                            <div className="klein leise">„{z.betreff}"</div>
                          )}
                        </td>
                        <td>
                          <span className={'marke-zustand '
                            + (z.art === 'unbekannt' ? 'z-neu' : 'z-laufend')}>
                            {arten[z.art] ?? z.art}
                          </span>
                          {/* Das Auswahlfeld steht NUR da, wo es gebraucht
                              wird: bei Zeilen, die ohnehin einen Menschen
                              brauchen. Fünf gleiche Auswahlfelder unter fünf
                              Zeilen sind fünfmal dieselbe Frage, und bei einer
                              richtig erkannten und zugeordneten Datei ist sie
                              schon beantwortet. */}
                          {zustand === 'offen' && darfEinsortieren
                           && !(sicher || tabelle) && (
                            <select
                              className="klein" style={{ marginTop: 5, width: '100%' }}
                              value={z.art}
                              onChange={async (e) => {
                                setLaeuft(true);
                                try { await api.eingangArt(z.id, e.target.value); await laden(); }
                                catch (f) { setFehler(ausFehler(f)); }
                                finally { setLaeuft(false); }
                              }}
                            >
                              {Object.entries(arten).map(([w, t]) => (
                                <option key={w} value={w}>{t}</option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td>
                          {tabelle ? (
                            <span className="klein leise">
                              Eine Tabelle gehört zu keiner einzelnen Akte.
                            </span>
                          ) : sicher ? (
                            <>
                              <b>{z.strasse} {z.hausnummer}{z.hausnummer_zusatz ?? ''}</b>
                              <div className="klein leise">
                                {z.plz} {z.ort_text}
                                {z.kls_id ? `, KLS ${z.kls_id}` : ''}
                              </div>
                              {z.zuordnung_wie && (
                                <div className="klein leise">{z.zuordnung_wie}</div>
                              )}
                            </>
                          ) : (
                            /* DER GRUND, NICHT NUR DER ZUSTAND. Ein „nicht
                               zugeordnet" ohne Grund lässt den Innendienst
                               raten, und beim nächsten Mal wieder. */
                            <span className="klein" style={{ color: 'var(--neu)' }}>
                              {z.grund ?? 'Nicht zugeordnet.'}
                            </span>
                          )}
                        </td>
                        <td className="klein leise">
                          {zeitpunkt(z.angekommen_am)}
                          {z.angekommen_von_name && (
                            <div>{z.angekommen_von_name}</div>
                          )}
                        </td>
                        {zustand === 'offen' && darfEinsortieren && (
                          <td>
                            <div className="reihe">
                              {sicher && !tabelle && (
                                <button className="knopf klein" disabled={laeuft}
                                        onClick={() => einsortieren(z)}>
                                  Ablegen
                                </button>
                              )}
                              {/* WER SONST SOLL ES ENTSCHEIDEN.
                                  Die Liste zeigt nur noch, was der Server
                                  nicht zuordnen konnte. Ohne diesen Knopf
                                  bliebe als einzige Handlung „Verwerfen", und
                                  dann verschwindet ein Bescheid, weil das
                                  Formular keinen anderen Ausweg anbot. */}
                              {!sicher && !tabelle && (
                                <button className="knopf klein" disabled={laeuft}
                                        onClick={() => setZuordnen(z)}>
                                  Zuordnen
                                </button>
                              )}
                              {tabelle && (
                                <button className="knopf klein haupt"
                                        disabled={laeuft}
                                        onClick={() => setTabellen([z.id])}>
                                  Einlesen
                                </button>
                              )}
                              {/* ====================================
                                  WER DIE ERKENNUNG VERBESSERT, TUT ES AN
                                  DEM PAPIER, DAS VOR IHM LIEGT.
                                  ====================================
                                  ORIGINALTEXT MARVIN (22.09.2026)
                                    „da soll die datei eingelesen werden und
                                     wenn er sie dann nicht zuordnen kann,
                                     dann kommt der leser, den ich öffnen
                                     kann."

                                  HIER STANDEN 2 BEDINGUNGEN, DIE GENAU DEN
                                  FALL AUSSPERRTEN, FÜR DEN ES DEN LESER
                                  GIBT: die Art musste schon als
                                  Behördenpapier erkannt sein, und ein
                                  Mailabsender musste dastehen. Eine Datei,
                                  die nicht zugeordnet werden konnte, hat
                                  meist beides nicht - **der Knopf verlangte,
                                  dass der Leser es schon kann.**

                                  Er steht jetzt an jedem Papier, das keine
                                  Tabelle ist. Art und Absender setzt der
                                  Leser selbst. */}
                              {/* ANSEHEN UND HERUNTERLADEN (.294). Marvin:
                                  „es wäre hilfreich, wenn ich die datei in
                                  einer vorschau öffnen kann und ggf.
                                  downloaden". Eine PDF geht wie überall im
                                  Programm des Geräts auf. */}
                              <a className="knopf klein" target="_blank" rel="noopener"
                                 href={api.eingangDateiAdresse(z.id, z.dateiname)}>
                                Ansehen
                              </a>
                              <a className="knopf klein" download={z.dateiname}
                                 href={api.eingangDateiAdresse(z.id, z.dateiname, true)}>
                                Herunterladen
                              </a>
                              {darfAnlernen && !tabelle && (
                                <button className="knopf klein" disabled={laeuft}
                                        onClick={() => setAnlernen(z)}>
                                  Leser anlernen
                                </button>
                              )}
                              <button className="knopf klein" disabled={laeuft}
                                      onClick={() => setVerwerfen(z)}>
                                Verwerfen
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* DER LAUFSTAND GEHÖRT UNTER DIE LISTE, NICHT IN DIE ÜBERSCHRIFT.
            Hier stand stattdessen ein Absatz, der bei jedem Aufruf erklärte,
            wie oft der Server nachsieht und welches Token der Mailweg
            braucht - eine Auskunft, die man einmal liest. Was hier fehlte:
            wann er ZULETZT gelaufen ist und was dabei herauskam. */}
        <p className="klein leise" style={{ marginTop: 14 }}>{laufsatz(d)}</p>

        {/* DIE EINMALIGEN UPLOADS SIND WEG.
            Marvin, 01.10.2026: „diese sachen zum einmaligen upload können
            weg." Die Übernahme aus der alten Tabelle und der Ordnerabwurf
            haben ihren Dienst getan; was noch an alten Ordnern kommt, geht
            durch das Transferverzeichnis - und das legt fehlende Aufträge
            jetzt selbst an. */}
        {/* UND DER BRIEFKASTEN FÜR DIE MASSE. Vierhundert Ordner durch die
            Abwurffläche heisst vierhundertmal zippen; er füllt sie über
            WinSCP, der Lauf steht trotzdem hier. */}
        {darf(ich, 'import.anwenden') && <Transferverzeichnis />}
      </div>

      {zuordnen && (
        <AuftragWaehlen
          eingang={zuordnen}
          zu={() => setZuordnen(null)}
          fertig={async (auftragId: string) => {
            await einsortieren(zuordnen, auftragId);
            setZuordnen(null);
          }}
        />
      )}

      {anlernen && (
        <Leseregel
          eingang={anlernen}
          zu={() => { setAnlernen(null); laden(); }}
        />
      )}

      {tabellen && (
        <ImportVorschau
          ich={ich} eingangIds={tabellen}
          zu={() => setTabellen(null)}
          fertig={async () => { setTabellen(null); await laden(); }}
        />
      )}

      {verwerfen && (
        <MitGrund
          titel={`„${verwerfen.dateiname}" verwerfen`}
          knopf="Verwerfen"
          erklaerung="Der Grund steht später neben der Datei. Ohne ihn fragt beim
                      nächsten Mal jemand, wo der Bescheid geblieben ist. Der
                      Inhalt bleibt in der Ablage."
          laeuft={laeuft} fehler={fehler}
          zu={() => setVerwerfen(null)}
          fertig={async (grund: string) => {
            setLaeuft(true);
            try {
              await api.eingangVerwerfen(verwerfen.id, grund);
              setVerwerfen(null);
              await laden();
            } catch (f) { setFehler(ausFehler(f)); }
            finally { setLaeuft(false); }
          }}
        />
      )}
    </>
  );
}


// ============================================================ Auftrag wählen

/**
 * Den Auftrag von Hand suchen.
 *
 * Gesucht wird über dieselbe Liste wie überall: Adresse, Ort, KLS, SM-Nummer,
 * Kunde. Kein eigener Suchweg für diesen Bildschirm.
 *
 * Es steht ausdrücklich dabei, WARUM die Datei liegen geblieben ist. Wer von
 * Hand zuordnet, soll wissen, woran die Maschine gescheitert ist: „passt auf
 * zwei Aufträge" verlangt eine andere Prüfung als „keine Anschrift im Text".
 */
function AuftragWaehlen({ eingang, zu, fertig }: any) {
  const [suche, setSuche] = useState('');
  const [treffer, setTreffer] = useState<any[]>([]);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  useEffect(() => {
    const p = new URLSearchParams({ proSeite: '15' });
    if (suche.trim()) p.set('suche', suche.trim());
    const zeit = setTimeout(() => {
      api.auftraege(p).then((d: any) => setTreffer(d.zeilen ?? [])).catch(() => setTreffer([]));
    }, 250);
    return () => clearTimeout(zeit);
  }, [suche]);

  return (
    <Dialog titel={`„${eingang.dateiname}" zuordnen`} laeuft={laeuft} zu={zu}
            breite={640}>
      <Fehlerkasten lage={fehler} />
      <div className="hinweis" style={{ marginBottom: 10 }}>
        <b>Warum sie liegen geblieben ist</b>
        {eingang.grund ?? 'Die Erkennung war nicht sicher genug.'}
      </div>
      <Text name="suche" titel="Suche" breite="100%" wert={suche} setze={setSuche}
            hinweis="Adresse, Ort, KLS-ID, SM-Nummer oder Kunde." />
      <table className="liste" style={{ marginTop: 10 }}>
        <tbody>
          {treffer.length === 0 && (
            <tr style={{ cursor: 'default' }}>
              <td className="klein leise">Kein Auftrag gefunden.</td>
            </tr>
          )}
          {treffer.map((a: any) => (
            <tr key={a.id} onClick={async () => {
              setLaeuft(true); setFehler(null);
              try { await fertig(a.id); }
              catch (f) { setFehler(ausFehler(f)); setLaeuft(false); }
            }}>
              <td>
                <b>{a.strasse} {a.hausnummer}{a.hausnummer_zusatz ?? ''}</b>
                <div className="klein leise">
                  {a.plz} {a.ort_text}
                  {a.kls_id ? `, KLS ${a.kls_id}` : ''}
                  {a.kunde_name ? `, ${a.kunde_name}` : ''}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Dialog>
  );
}
